/**
 * Default permission templates for the three platform roles.
 *
 * These are used in two places:
 *  1. mockUserRoleService.getUserRoleById — seeds permissions for system roles
 *     when no localStorage data exists (fresh onboarding / new organisation).
 *  2. EditRolePage — "Apply Default Template" button so an admin can one-click
 *     restore a system role to its intended baseline.
 *
 * ⚠️  Engineering / Devin — Action Required
 *  The real API should return equivalent default permissions for system roles
 *  when a new organisation is provisioned. See docs/features/roles-and-permissions.md
 *  for the full backend seeding requirements.
 */

import type { UserRolePermissions } from './userRoleService';

// ---------------------------------------------------------------------------
// Administrator — full access to everything
// ---------------------------------------------------------------------------
export const ADMINISTRATOR_DEFAULT_PERMISSIONS: UserRolePermissions = {
  permissions: ['dashboard.view_own'],

  timesheetAccess: {
    viewAccess: 'all',
    editAccess: 'all',
    allowDelete: true,
    allowDuplicate: true,
    allowRecall: true,
  },

  smartFeaturesPermissions: {
    enableCopyFromLastWeek: true,
    enableCopyDescription: true,
    enableCopyOnlyBillable: true,
    enableAutoSmartFill: true,
    enableAiInsights: true,
  },

  approvalPermissions: {
    viewDashboard: true,
  },

  timesheetApprovalPermissions: {
    viewApprovals: true,
    editTimesheets: true,
    rejectTimesheets: true,
    compulsoryRejectionReason: true,
    bulkApprove: true,
    delegateApproval: true,
    viewPolicyViolations: true,
    viewDetectedIssues: true,
  },

  reportAccessPermissions: {
    timesheetReport:         { enabled: true, viewAccess: 'all', allowExport: true },
    missingTimesheetReport:  { enabled: true, viewAccess: 'all', allowExport: true },
    utilisationReport:       { enabled: true, viewAccess: 'all', allowExport: true },
    // billableHoursReport removed — belongs in the Projects module of the PSA suite
  },

  employeeDirectoryPermissions: {
    viewDirectory: true,
    viewAccess: 'all',
    viewDetails: true,
  },

  integrationsPermissions: {
    viewIntegrations: true,
    manageIntegrations: true,
  },

  companyManagementPermissions: {
    viewCompanies: true,
    manageCompanies: true,
  },

  systemSettingsPermissions: {
    manageGeneralSettings: 'configure',
    manageDeadlines: true,
    managePolicySettings: true,
    manageUtilisationSettings: true,
    manageTimeEntrySettings: true,
    manageWorkTypeSettings: true,
    manageApprovalsSettings: true,
    manageNotifications: true,
    manageRolesPermissions: true,
    manageScreenDataModes: false, // hidden pending Settings menu — see docs
  },
};

// ---------------------------------------------------------------------------
// User (Employee) — standard time-entry access only
// ---------------------------------------------------------------------------
export const USER_DEFAULT_PERMISSIONS: UserRolePermissions = {
  permissions: ['dashboard.view_own'],

  timesheetAccess: {
    viewAccess: 'own',
    editAccess: 'own',
    allowDelete: false,
    allowDuplicate: false,
    allowRecall: true,
  },

  smartFeaturesPermissions: {
    enableCopyFromLastWeek: true,
    enableCopyDescription: true,
    enableCopyOnlyBillable: false,
    enableAutoSmartFill: true,
    enableAiInsights: false,
  },

  approvalPermissions: {
    viewDashboard: false,
  },

  timesheetApprovalPermissions: {
    viewApprovals: false,
    editTimesheets: false,
    rejectTimesheets: false,
    compulsoryRejectionReason: false,
    bulkApprove: false,
    delegateApproval: false,
    viewPolicyViolations: false,
    viewDetectedIssues: false,
  },

  reportAccessPermissions: {
    timesheetReport:         { enabled: true,  viewAccess: 'own', allowExport: false },
    missingTimesheetReport:  { enabled: false, viewAccess: 'own', allowExport: false },
    utilisationReport:       { enabled: false, viewAccess: 'own', allowExport: false },
    // billableHoursReport removed — belongs in the Projects module of the PSA suite
  },

  employeeDirectoryPermissions: {
    viewDirectory: true,
    viewAccess: 'own',
    viewDetails: true,
  },

  integrationsPermissions: {
    viewIntegrations: false,
    manageIntegrations: false,
  },

  companyManagementPermissions: {
    viewCompanies: false,
    manageCompanies: false,
  },

  systemSettingsPermissions: {
    manageGeneralSettings: 'view',
    manageDeadlines: false,
    managePolicySettings: false,
    manageUtilisationSettings: false,
    manageTimeEntrySettings: false,
    manageWorkTypeSettings: false,
    manageApprovalsSettings: false,
    manageNotifications: false,
    manageRolesPermissions: false,
    manageScreenDataModes: false,
  },
};

// ---------------------------------------------------------------------------
// Approver — time-entry + full approval access + team/manager reporting
// ---------------------------------------------------------------------------
export const APPROVER_DEFAULT_PERMISSIONS: UserRolePermissions = {
  permissions: ['dashboard.view_own'],

  timesheetAccess: {
    viewAccess: 'team',
    editAccess: 'own',
    allowDelete: false,
    allowDuplicate: true,
    allowRecall: true,
  },

  smartFeaturesPermissions: {
    enableCopyFromLastWeek: true,
    enableCopyDescription: true,
    enableCopyOnlyBillable: false,
    enableAutoSmartFill: true,
    enableAiInsights: true,
  },

  approvalPermissions: {
    viewDashboard: true,
  },

  timesheetApprovalPermissions: {
    viewApprovals: true,
    editTimesheets: true,
    rejectTimesheets: true,
    compulsoryRejectionReason: true,
    bulkApprove: true,
    delegateApproval: true,
    viewPolicyViolations: true,
    viewDetectedIssues: true,
  },

  reportAccessPermissions: {
    timesheetReport:         { enabled: true, viewAccess: 'team', allowExport: true },
    missingTimesheetReport:  { enabled: true, viewAccess: 'team', allowExport: true },
    utilisationReport:       { enabled: true, viewAccess: 'team', allowExport: false },
    // billableHoursReport removed — belongs in the Projects module of the PSA suite
  },

  employeeDirectoryPermissions: {
    viewDirectory: true,
    viewAccess: 'team',
    viewDetails: true,
  },

  integrationsPermissions: {
    viewIntegrations: false,
    manageIntegrations: false,
  },

  companyManagementPermissions: {
    viewCompanies: false,
    manageCompanies: false,
  },

  systemSettingsPermissions: {
    manageGeneralSettings: 'view',
    manageDeadlines: false,
    managePolicySettings: false,
    manageUtilisationSettings: false,
    manageTimeEntrySettings: false,
    manageWorkTypeSettings: false,
    manageApprovalsSettings: false,
    manageNotifications: false,
    manageRolesPermissions: false,
    manageScreenDataModes: false,
  },
};

// ---------------------------------------------------------------------------
// Lookup helper — returns the default permissions for a given system role id
// IDs 1, 2, 3 match defaultMockUserRoles in userRoleService.ts
// ---------------------------------------------------------------------------
export const DEFAULT_ROLE_PERMISSIONS_MAP: Record<number, UserRolePermissions> = {
  1: ADMINISTRATOR_DEFAULT_PERMISSIONS,
  2: USER_DEFAULT_PERMISSIONS,
  3: APPROVER_DEFAULT_PERMISSIONS,
};

export type DefaultRoleType = 'administrator' | 'user' | 'approver';

export const ROLE_TEMPLATE_LABELS: Record<DefaultRoleType, string> = {
  administrator: 'Administrator',
  user: 'User',
  approver: 'Approver',
};
